import React from 'react';
import { X as CloseIcon } from 'lucide-react';
import { useFilterStore } from '@/lib/FilterStore';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Cuisine, Skill } from '@/lib/types';

interface ActiveFilterChipsProps {
  className?: string;
  onResetAll?: () => void;
  cuisinesOverride?: Cuisine[];
}

/**
 * ActiveFilterChips - Premium chip display for active filters
 * 
 * Redesign 2026:
 * - REMOVED delivery mode chip (redundant with DeliveryModeToggle in header)
 * - REMOVED legacy deliveryOnly chip (same reason)
 * - Kept: cuisines, skills, openNow, distance, search, location
 */
export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  className,
  onResetAll,
  cuisinesOverride
}) => {
  const filterStore = useFilterStore();
  
  const { data: cuisines = [] } = useQuery<Cuisine[]>({
    queryKey: queryKeys.cuisines.list(),
    queryFn: async () => {
      const response = await fetch('/api/cuisines', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch cuisines');
      return response.json();
    },
    staleTime: 30 * 60 * 1000,
  });
  
  const { data: skills = [] } = useQuery<Skill[]>({
    queryKey: queryKeys.skills.list(),
    queryFn: async () => {
      const response = await fetch('/api/skills', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch skills');
      return response.json();
    },
    staleTime: 30 * 60 * 1000,
  });
  
  const effectiveCuisines = cuisinesOverride && cuisinesOverride.length > 0
    ? cuisinesOverride
    : cuisines;

  // Build active filter chips — NO delivery mode, NO deliveryOnly (shown in header toggle)
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  
  // Cuisines
  filterStore.cuisineIds.forEach(id => {
    const cuisine = effectiveCuisines.find(c => c.id === id);
    if (cuisine) {
      chips.push({
        key: `cuisine-${id}`,
        label: cuisine.name,
        onRemove: () => {
          const newCuisines = filterStore.cuisineIds.filter(cid => cid !== id);
          filterStore.setCuisines(newCuisines);
        }
      });
    }
  });
  
  // Skills (Dietary)
  filterStore.skills.forEach(id => {
    const skill = skills.find(s => s.id === id);
    if (skill) {
      chips.push({
        key: `skill-${id}`,
        label: skill.name,
        onRemove: () => {
          const newSkills = filterStore.skills.filter(sid => sid !== id);
          filterStore.setSkills(newSkills);
        }
      });
    }
  });
  
  // Open Now
  if (filterStore.openNow) {
    chips.push({
      key: 'open-now',
      label: 'Jetzt geöffnet',
      onRemove: () => filterStore.setOpenNow(false)
    });
  }
  
  // Distance (only if not default)
  if (filterStore.distance !== 20) {
    chips.push({
      key: 'distance',
      label: `${filterStore.distance} km`,
      onRemove: () => filterStore.setDistance(20)
    });
  }
  
  // Search Query
  const searchQuery = (filterStore.query || filterStore.searchQuery || '').trim();
  if (searchQuery) {
    chips.push({
      key: 'search',
      label: `"${searchQuery}"`,
      onRemove: () => filterStore.setQuery('')
    });
  }
  
  // Location
  if (filterStore.location?.label && filterStore.location.label.trim()) {
    chips.push({
      key: 'location',
      label: filterStore.location.label,
      onRemove: () => filterStore.setLocation(null)
    });
  }
  
  const hasChips = chips.length > 0;
  
  if (!hasChips) return null;
  
  const handleResetAll = () => {
    filterStore.resetAll();
    onResetAll?.();
  };
  
  const getChipMaxWidth = (chipKey: string) => {
    if (chipKey === 'location') return 'max-w-[150px]';
    if (chipKey === 'search') return 'max-w-[120px]';
    return 'max-w-[120px]';
  };
  
  return (
    <div 
      className={cn(
        "overflow-visible",
        "ActiveFiltersBar",
        className
      )}
      style={{
        maxWidth: '100vw',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        position: 'relative',
        zIndex: 10
      }}
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-2 min-w-0">
          <div 
            className="flex flex-nowrap items-center gap-1.5 flex-1 min-w-0 overflow-x-auto scrollbar-hide py-1.5" 
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {chips.map(chip => (
              <button
                key={chip.key}
                onClick={chip.onRemove}
                title={chip.label}
                className={cn(
                  "inline-flex items-center gap-1 px-2.5 py-1 rounded-full",
                  "bg-[#009544]/10 text-[#009544]",
                  "hover:bg-[#009544]/15",
                  "transition-colors duration-200",
                  "flex-shrink-0 whitespace-nowrap",
                  "text-xs font-medium",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009544] focus-visible:ring-offset-1",
                  "active:scale-95"
                )}
                aria-label={`Filter entfernen: ${chip.label}`}
              >
                <span className={cn("truncate", getChipMaxWidth(chip.key))}>{chip.label}</span>
                <CloseIcon className="w-3 h-3 flex-shrink-0 opacity-60" />
              </button>
            ))}
          </div>
          
          {chips.length > 1 && (
            <button
              onClick={handleResetAll}
              title="Alle Filter löschen"
              className={cn(
                "text-xs text-gray-400 hover:text-red-500 font-medium transition-colors flex-shrink-0 px-1",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009544]"
              )}
              aria-label="Alle Filter löschen"
            >
              Alle löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
